import { computed, nextTick, reactive, ref } from "vue";
import { defineStore } from "pinia";
import { toBoolean } from "#ce/utils/props";
import { useFlash } from "#ce/composables/useFlash";
import { createAssignSpecialtiesApi } from "#ce/apps/SpAdmin/SpAssignSpecialtiesApp/api/assign-specialties";
import { tokenize } from "#ce/apps/SpAdmin/SpAssignSpecialtiesApp/utils/term-parser";
import { deepMerge, parseJson } from "#ce/apps/SpAdmin/shared/utils/config";
import { isAcceptedFile } from "#ce/apps/SpAdmin/shared/utils/file-acceptance";
import { pluralizeTemplate } from "#ce/apps/SpAdmin/shared/utils/pluralize";

function defaultConfig() {
  return {
    previewUrl: "",
    processUrl: "",
    clientName: "",
    specialties: "[]",
    dropzoneText: "Drag and drop spreadsheet here, or <strong>click to browse</strong>",
    fileSpecHtml: "",
    termInputLabel: "Terms (separated by |)",
    modeAddLabel: "Add to existing specialties",
    modeReplaceLabel: "Replace existing specialties",
    modeLabel: "For existing terms",
    specialtySelectLabel: "Specialties to assign",
    flagToggleLabel: "Keep specialties under {client} control",
    clientControlEnabled: false,
    outcomeLabels: "",
    statusLabels: "",
    labels: "{}",
    modalTitle: "Assign Specialties",
  };
}

// Minimal English defaults so specs and Storybook work standalone; production
// always deep-merges the helper's localized `labels` JSON over these.
function labelDefaults(client) {
  return {
    preview: "Preview",
    back: "Back",
    confirm: "Assign",
    orDivider: "or",
    termInputPlaceholder: "Knee Pain | Sports Medicine",
    invalidEntry: "Invalid entry",
    removedSpecialty: "Removed: %{name}",
    termsEntered: { one: "%{count} term entered", other: "%{count} terms entered" },
    columns: {
      status: "Status",
      name: "Name",
      type: "Type",
      resultingSpecialties: "Resulting specialties",
      control: "Control",
    },
    errors: {
      unsupportedFile: "Unsupported file type. Please use .csv, .xlsx, or .xls files.",
      noTerms: "Please provide terms via text or file upload.",
      noSpecialties: "Please select at least one specialty to assign.",
      previewFailed: "An error occurred while previewing. Please try again.",
      processFailed: "An error occurred while processing. Please try again.",
    },
    summary: {
      new: { one: "%{count} new term will be requested.", other: "%{count} new terms will be requested." },
      existing: { one: "%{count} existing will be updated.", other: "%{count} existing will be updated." },
      willEnable: {
        one: `%{count} will enable Use ${client} Specialties.`,
        other: `%{count} will enable Use ${client} Specialties.`,
      },
      errors: { one: "%{count} with errors.", other: "%{count} with errors." },
    },
    flash: {
      queued: {
        one: "Assigning specialties to %{count} term in the background — the results will appear shortly.",
        other: "Assigning specialties to %{count} terms in the background — the results will appear shortly.",
      },
      created: { one: "%{count} new term requested.", other: "%{count} new terms requested." },
      updated: { one: "%{count} term updated.", other: "%{count} terms updated." },
      flagEnabled: {
        one: `%{count} now under ${client} control.`,
        other: `%{count} now under ${client} control.`,
      },
      skipped: { one: "%{count} skipped.", other: "%{count} skipped." },
    },
  };
}

export const navigation = {
  reloadPage() {
    window.location.reload();
  },
};

export const useAssignSpecialtiesStore = defineStore("assignSpecialties", () => {
  const api = createAssignSpecialtiesApi();
  const flash = useFlash();

  const config = reactive(defaultConfig());

  // Guards against stale async responses: an in-flight preview/confirm started
  // before reset()/close() (or superseded by a newer request) must not apply
  // its response over newer state. Non-reactive — only compared, never rendered.
  let requestGeneration = 0;

  // STATE:
  const isOpen = ref(false);
  // Bumped on every open() so per-open UI (e.g. the dropzone's local drag/spec
  // state) can be remounted fresh via a :key binding.
  const openGeneration = ref(0);
  const currentStep = ref("input");
  const inputText = ref("");
  const terms = ref([]);
  const termFile = ref(null);
  const selectedSpecialtyIds = ref([]);
  const mode = ref("add");
  const enableClientSpecialties = ref(true);
  const previewData = ref(null);
  const isSubmitting = ref(false);
  const errorMessage = ref("");
  const completedCount = ref(0);

  // COMPUTED:
  const clientLabel = computed(() => config.clientName || "client");
  const specialtyOptions = computed(() => parseJson(config.specialties, []));
  const flagToggleLabel = computed(() => config.flagToggleLabel.replaceAll("{client}", clientLabel.value));
  // Whether this deployment manages client-controlled specialties (canonical MST
  // management). When off, every client-control surface (toggle, Control column,
  // will-enable summary fragment) is hidden and the flag is submitted as false.
  const clientControlEnabled = computed(() => toBoolean(config.clientControlEnabled));
  const outcomeLabels = computed(() => ({
    will_enable: `Use ${clientLabel.value} Specialties`,
    unchanged: "Unchanged",
    new_term: `${clientLabel.value} (User Added)`,
    ...parseJson(config.outcomeLabels, {}),
  }));
  const statusLabels = computed(() => ({
    new: "New term, will be requested",
    existing: "Existing term, will be updated",
    ...parseJson(config.statusLabels, {}),
  }));
  // Localized UI copy from the helper, deep-merged over the English defaults.
  const labels = computed(() => deepMerge(labelDefaults(clientLabel.value), parseJson(config.labels, {})));
  // The flag the backend receives: the user's toggle only counts when the
  // deployment supports client control at all.
  const clientSpecialtiesRequested = computed(() => clientControlEnabled.value && enableClientSpecialties.value);
  const assignableRows = computed(() => (previewData.value?.rows ?? []).filter((row) => row.status !== "error"));
  const confirmDisabled = computed(() => isSubmitting.value || assignableRows.value.length === 0);

  // ACTIONS:
  function configure(nextConfig = {}) {
    Object.assign(config, defaultConfig(), nextConfig);
  }

  function reset() {
    requestGeneration += 1;
    currentStep.value = "input";
    inputText.value = "";
    terms.value = [];
    termFile.value = null;
    selectedSpecialtyIds.value = [];
    mode.value = "add";
    enableClientSpecialties.value = true;
    previewData.value = null;
    isSubmitting.value = false;
    errorMessage.value = "";
  }

  function open(overrides = {}) {
    Object.assign(config, overrides);
    openGeneration.value += 1;
    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
    reset();
  }

  function addTerms(text) {
    const existing = new Set(terms.value.map((term) => term.toLowerCase()));

    for (const term of tokenize(text)) {
      if (!existing.has(term.toLowerCase())) {
        terms.value.push(term);
        existing.add(term.toLowerCase());
      }
    }
  }

  function removeTerm(index) {
    terms.value.splice(index, 1);
  }

  function commitInput(text = inputText.value) {
    if (!text?.trim()) {
      return;
    }

    addTerms(text);
    inputText.value = "";
  }

  function handleFileChange(file) {
    if (!file) {
      return;
    }

    if (!isAcceptedFile(file)) {
      // Also drop any previously attached valid file — keeping it while the
      // error shows would silently submit a file the user just tried to replace.
      termFile.value = null;
      errorMessage.value = labels.value.errors.unsupportedFile;
      return;
    }

    termFile.value = file;
    errorMessage.value = "";
  }

  function removeFile() {
    termFile.value = null;
  }

  async function preview() {
    commitInput();

    if (terms.value.length === 0 && !termFile.value) {
      errorMessage.value = labels.value.errors.noTerms;
      return false;
    }

    if (selectedSpecialtyIds.value.length === 0) {
      errorMessage.value = labels.value.errors.noSpecialties;
      return false;
    }

    requestGeneration += 1;
    const generation = requestGeneration;
    isSubmitting.value = true;
    errorMessage.value = "";

    const formData = new FormData();

    if (termFile.value) {
      formData.append("term_file", termFile.value);
    }

    if (terms.value.length > 0) {
      formData.append("term_text", terms.value.join("|"));
    }

    for (const id of selectedSpecialtyIds.value) {
      formData.append("specialty_ids[]", id);
    }

    formData.append("assign_mode", mode.value);
    formData.append("enable_client_specialties", clientSpecialtiesRequested.value ? "true" : "false");

    try {
      const data = await api.preview(config.previewUrl, formData);

      if (generation !== requestGeneration) {
        return false;
      }

      previewData.value = {
        rows: data.rows || [],
        summary: data.summary || { new: 0, existing: 0, will_enable: 0, errors: 0, total: 0 },
        errors: data.errors || [],
      };
      currentStep.value = "confirm";
      return true;
    } catch (error) {
      if (generation !== requestGeneration) {
        return false;
      }

      errorMessage.value = error.message || labels.value.errors.previewFailed;
      return false;
    } finally {
      // A stale request must not clear a newer request's submitting state.
      if (generation === requestGeneration) {
        isSubmitting.value = false;
      }
    }
  }

  async function confirm() {
    requestGeneration += 1;
    const generation = requestGeneration;
    isSubmitting.value = true;
    errorMessage.value = "";

    try {
      const data = await api.process(config.processUrl, {
        terms: assignableRows.value.map((row) => ({ name: row.name, type: row.type })),
        specialty_ids: selectedSpecialtyIds.value,
        assign_mode: mode.value,
        enable_client_specialties: clientSpecialtiesRequested.value,
      });

      if (generation !== requestGeneration) {
        return false;
      }

      // Compose the flash from server-provided templates so wording localizes
      // and adapts per deployment (e.g. non-canonical "added" vs "requested").
      // The assignment runs in the background (a synchronous run can outlive the
      // proxy timeout), so the normal response only acknowledges the queued run.
      const parts = [];

      if (data.queued) {
        parts.push(pluralizeTemplate(labels.value.flash.queued, Number(data.total || 0)));
      } else {
        const flagEnabled = Number(data.flag_enabled || 0);
        const skipped = Number(data.skipped || 0);
        parts.push(
          pluralizeTemplate(labels.value.flash.created, Number(data.created || 0)),
          pluralizeTemplate(labels.value.flash.updated, Number(data.updated || 0)),
        );

        if (clientControlEnabled.value && flagEnabled > 0) {
          parts.push(pluralizeTemplate(labels.value.flash.flagEnabled, flagEnabled));
        }

        if (skipped > 0) {
          parts.push(pluralizeTemplate(labels.value.flash.skipped, skipped));
        }
      }

      if (data.errors?.length > 0) {
        parts.push(data.errors.join(". "));
      }

      completedCount.value += 1;
      flash.success(parts.join(" "), { autoremoveDelay: 8000 });
      close();

      // Queued handoff keeps the page: the import hasn't run yet, and a reload would
      // destroy the client-side acknowledgement flash.
      if (!data.queued) {
        await nextTick();
        navigation.reloadPage();
      }

      return true;
    } catch (error) {
      if (generation !== requestGeneration) {
        return false;
      }

      errorMessage.value = error.message || labels.value.errors.processFailed;
      return false;
    } finally {
      // A stale request must not clear a newer request's submitting state. (The
      // success path bumps the generation itself via close() → reset(), which
      // also already reset isSubmitting.)
      if (generation === requestGeneration) {
        isSubmitting.value = false;
      }
    }
  }

  return {
    config,
    isOpen,
    openGeneration,
    currentStep,
    inputText,
    terms,
    termFile,
    selectedSpecialtyIds,
    mode,
    enableClientSpecialties,
    previewData,
    isSubmitting,
    errorMessage,
    completedCount,
    clientLabel,
    specialtyOptions,
    flagToggleLabel,
    clientControlEnabled,
    outcomeLabels,
    statusLabels,
    labels,
    assignableRows,
    confirmDisabled,
    configure,
    reset,
    open,
    close,
    addTerms,
    removeTerm,
    commitInput,
    handleFileChange,
    removeFile,
    preview,
    confirm,
  };
});
